import { useEffect, useState } from "react";
import { Alert, Center, Loader } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useParams } from "react-router-dom";

import { api } from "../../lib";
import {
  clearInternalRenderBootstrapToken,
  getInternalRenderBootstrapToken,
} from "../../lib/internalRenderBootstrap";
import { setRenderMode } from "../../lib/renderModeFlag";
import type {
  CptFrequencyExportData,
  SpecimenLogResponse,
  AccessionLogResponse,
} from "../../services/reports.service";
import type { CorrectionsReportData } from "../../services/correctionRequestService";
import { CptFrequencyPrintReport } from "./CptFrequencyPrintReport";
import { SpecimenLogPrintReport } from "./SpecimenLogPrintReport";
import { AccessionLogPrintReport } from "./AccessionLogPrintReport";
import { BillingReportCapture } from "./BillingReportCapture";
import { CorrectionsPrintReport } from "./CorrectionsPrintReport";

const TOKEN_HEADER = "X-Billing-Report-Render-Token";

interface RenderContext {
  reportType:
    | "cpt-frequency"
    | "specimen-log"
    | "accession-log"
    | "corrections";
  reportData:
    | CptFrequencyExportData
    | SpecimenLogResponse
    | AccessionLogResponse
    | CorrectionsReportData;
}

export default function RenderBillingReportPage() {
  const { reportType } = useParams<{ reportType: string }>();
  const [token] = useState(getInternalRenderBootstrapToken);
  const [context, setContext] = useState<RenderContext | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setRenderMode(true);
    clearInternalRenderBootstrapToken();
    if (!token || !reportType) {
      setFailed(true);
      return () => setRenderMode(false);
    }
    api
      .get<RenderContext>("/billing-report-render/context", {
        headers: { [TOKEN_HEADER]: token },
      })
      .then((value) => {
        if (!active) return;
        if (value.reportType !== reportType) {
          setFailed(true);
          return;
        }
        setContext(value);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
      setRenderMode(false);
    };
  }, [reportType, token]);

  if (failed) {
    return (
      <Center h="100vh" data-render-error="true">
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertCircle size={16} />}
          title="Report rendering failed"
          role="alert"
        >
          The billing report could not be rendered.
        </Alert>
      </Center>
    );
  }

  if (!context) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }

  return (
    <BillingReportCapture>
      {context.reportType === "cpt-frequency" && (
        <CptFrequencyPrintReport
          report={context.reportData as CptFrequencyExportData}
        />
      )}
      {context.reportType === "specimen-log" && (
        <SpecimenLogPrintReport
          report={context.reportData as SpecimenLogResponse}
        />
      )}
      {context.reportType === "accession-log" && (
        <AccessionLogPrintReport
          report={context.reportData as AccessionLogResponse}
        />
      )}
      {context.reportType === "corrections" && (
        <CorrectionsPrintReport
          report={context.reportData as CorrectionsReportData}
        />
      )}
    </BillingReportCapture>
  );
}
